import type React from 'react';
import { createContext } from 'react';
import type { Socket } from 'socket.io-client';
import {
  type BrandInsightsPageContext,
  type Chat,
  type ChatContextItem,
  type ChatContextLayout,
  type DisplayMode,
  type HcpSearchResult,
  type KolSearchResult,
} from '@trinity/chat';
import { createNamedContext } from '@trinity/chat-components/utils/react';
import type { Context, KolSearchResultSortKey, LandingCard } from './interfaces';

export const SubmitMessageContext = createContext<Context.SubmitMessage>(undefined);
export const ChatPageContextDataContext = createContext<Context.ChatPageContextData>(undefined);
export const DispatchPageContextDataContext = createContext<Context.DispatchPageContextData>(undefined);
export const AuthorContext = createContext<Context.Author>(undefined);
export const RegenerateMessageContext = createContext<Context.RegenerateMessage>(undefined);
export const VariantMessageContext = createContext<Context.VariantMessage>(undefined);
export const UploadFilesContext = createContext<Context.UploadFiles>(undefined);
export const IsPreparingUploadContext = createContext<boolean>(undefined);
export const SetPendingScrollContext = createContext<Context.SetPendingScroll>(undefined);
export const SubmitActionContext = createContext<Context.SubmitAction>(undefined);

export const ChatSessionFiltersContext = createContext<Chat.Filters>(undefined);
export const ChatSessionInputContext = createContext<string>(undefined);
export const ChatSessionPendingHintPromptIdentifierContext = createContext<string | null>(null);
export const ChatSessionQueriesContext = createContext<Record<string, Chat.Query>>(undefined);
export const ChatSessionFileUploadsContext = createContext<Chat.FileUpload[]>(undefined);
export const ChatSessionKolSearchesContext = createContext<Chat.KolSearch[]>(undefined);
export const ChatSessionPendingQueryContext = createContext<string | null>(undefined);
export const ActiveChatSessionContext = createNamedContext<Context.ActiveChatSession>(
  undefined,
  'ActiveChatSessionContext'
);
export const ChatSessionSelectedQueryContext = createContext<Chat.Query>(undefined);
export const ChatSettingsContext = createContext<Context.ChatSettings>(undefined);
export const ChatSessionItemsContext = createContext<Chat.InstanceItem[]>(undefined);

export const ChatInitialResponseContext = createContext<Chat.QueryResponse | null>(null);
export const ChatHintsLoadingContext = createContext<boolean>(undefined);

export const ChatPermissionsContext = createContext<Context.ChatPermissions>(undefined);

export const MessagesListRefContext = createContext<Context.MessagesListRef>([null, () => {}]);

export const ChatSettingsToggleContext = createContext<Context.ChatSettingsToggle>(undefined);
export const MessageCitationsToggleContext = createContext<Context.MessageCitationsToggle>(undefined);
export const OpenSnippetContext = createContext<Context.OpenSnippet>(undefined);
export const ClickCitationContext = createContext<Context.ClickCitation>(undefined);
export const AssignCitationRefContext = createContext<Context.AssignCitationRef>(undefined);
export const CitationsContext = createContext<Chat.Citation[]>(undefined);
export const OpenCrmNoteContext = createContext<string>(undefined);
export const SetOpenCitationContext = createContext<Context.SetOpenCitation>(undefined);

export const ChatHasAccessContext = createContext<boolean>(true);

export const QuerySocketContext: React.Context<Socket | undefined> = createContext<Socket | undefined>(
  undefined
);

export const KolSearchIdContext = createContext<number>(undefined);
export const KolSearchIdentifierContext = createContext<string>(undefined);
export const KolSearchFormContext = createContext<Context.KolSearchFormState>(undefined);
export const DispatchKolSearchFormContext = createContext<Context.DispatchKolSearchForm>(undefined);
export const KolSearchStatusContext = createContext<Context.KolSearchStatus>(undefined);
export const KolSearchSubmitContext = createContext<Context.KolSearchSubmit>(undefined);
export const KolSearchResultsContext = createContext<Context.KolSearchResults>(undefined);
export const KolSearchCancelContext = createContext<Context.KolSearchCancel>(undefined);
export const KolResetSearchContext = createContext<Context.KolSearchCancel>(undefined);
export const KolProfileContext = createContext<Context.KolSearchProfile>(undefined);
export const UpdateKolSearchProfileContext = createContext<Context.UpdateKolSearchProfile>(undefined);
export const KolSearchErrorTraceIdContext = createContext<string>(undefined);
export const KolFollowupQuestionsContext = createContext<string[]>([]);
export const KolSearchFormBeginContext = createContext<Context.KolSearchFormBegin>(undefined);
export const KolSearchRecommendationsContext = createContext<Context.KolSearchRecommendations>(undefined);
export const KolSearchRecommendationProfileSelectedContext =
  createContext<Context.KolSearchRecommendationProfileSelected>(undefined);

export const KolSearchResultsSortContext = createNamedContext<
  Context.TableSortContextData<KolSearchResultSortKey>
>(undefined, 'kol-search-results-sort-context');
export const KolSearchResultsFilterContext = createNamedContext<Context.TableFilterContextData>(
  undefined,
  'kol-search-results-filter-context'
);
export const KolSearchResultsSelectionContext = createNamedContext<Context.KolTableSelectionContext>(
  undefined,
  'kol-search-results-selection-context'
);
export const KolSearchResultsPageSizeContext = createNamedContext<Context.PageSize>(
  undefined,
  'kol-search-results-page-size-context'
);
export const KolSearchResultsPageIndexContext = createNamedContext<Context.PageIndex>(
  undefined,
  'kol-search-results-page-index-context'
);
export const KolSearchResultsSetPageIndexContext = createNamedContext<Context.SetPageIndex>(
  undefined,
  'kol-search-results-set-page-index-context'
);
export const KolSearchResultsSetPageSizeContext = createNamedContext<Context.SetPageSize>(
  undefined,
  'kol-search-results-set-page-size-context'
);
export const KolSearchTablePageContext = createNamedContext<Context.TablePageResults>(
  undefined,
  'kol-search-results-context'
);
export const FetchMoreResultsContext = createNamedContext<Context.FetchMoreResults>(
  undefined,
  'kol-search-results-fetch-more-context'
);

export const LegendCollapseToggleContext = createContext<Context.LegendCollapseToggle>(undefined);
export const ChartRefContext = createContext<Context.ChartRef>(undefined);
export const ChartInfoContext = createContext<Context.ChartInfo>(undefined);
export const ChartGroupByContext = createContext<Context.ChartGroupByContext>(undefined);
export const ChartTypeContext = createContext<Context.ChartType>(undefined);
export const ChartRecordIdContext = createContext<number | null>(null);
export const ChartViewContext = createContext<Context.ChartView>(undefined);
export const ChartThemeIdContext = createContext<Context.ChartThemeId>(undefined);

export const ChatContextItemsContext = createContext<ChatContextItem[]>([]);
export const ChatContextItemsLoadingContext = createContext<boolean>(false);
export const ChatDefaultContextItemContext = createContext<BrandInsightsPageContext.Item | null>(null);
export const ChatFaqContextItemContext = createContext<ChatContextItem | null>(null);
export const AttachmentsContext = createContext<Chat.Attachment.Item[]>([]);
export const AttachmentItemRendererContext = createContext<React.ComponentType<{ id: string }> | null>(null);

export type ChatInputFeatures = {
  fileUpload?: boolean;
  voiceInput?: boolean;
};

export const ChatInputFeaturesContext = createNamedContext<ChatInputFeatures>(
  {},
  'chat-input-features-context'
);

export const ChatLandingCardsVisibleContext = createContext<boolean>(true);
export const ChatLandingCardsLoadingContext = createContext<boolean>(false);
export const ChatLandingCardsContext = createContext<LandingCard[]>([]);

export const DisplayModeContext = createContext<DisplayMode>('full');
export const ToggleDisplayModeContext = createContext<(() => void) | null>(null);
export const SetDisplayModeContext = createContext<((mode: DisplayMode) => void) | null>(null);

// Actions Context for contextual actions in table rows
export type ActionsContextValue = Record<string, () => void>;

export const ActionsContext = createNamedContext<ActionsContextValue>(undefined, 'ActionsContext');

// Smart Routing HCP contexts
export type SmartRoutingHcpContextValue = {
  searchResults: Array<KolSearchResult | HcpSearchResult>;
  fetchedHcp: HcpSearchResult | KolSearchResult | null;
  isSearchLoading: boolean;
  hasSearchCompleted: boolean;
  startingLocation: Chat.QueryState.SmartRouting['startingLocation'];
};

export const SmartRoutingHcpContext = createContext<SmartRoutingHcpContextValue>({
  searchResults: [],
  fetchedHcp: null,
  isSearchLoading: false,
  hasSearchCompleted: false,
  startingLocation: null,
});
export const SmartRoutingHcpSetterContext = createContext<
  (hcpResults: Array<KolSearchResult | HcpSearchResult>) => void
>(() => {});

// Active page context — when set, the chat screen shows a page instead of messages.
// Cleared when the user submits a query, transitioning to the chat view.
export type ActivePageContextValue = {
  pageId: number | null;
  layout: ChatContextLayout;
  clearPage: () => void;
  setPage: (pageId: number | null, layout?: ChatContextLayout) => void;
};

export const ActivePageContext = createContext<ActivePageContextValue>({
  pageId: null,
  layout: 'full_page',
  clearPage: () => {},
  setPage: () => {},
});
